import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "./api";

const AuthContext = createContext(null);

const ROLE_LABEL = {
    CORPORATE_ADMIN: "Corporate Admin",
    FINANCE_PRICING: "Finance · Pricing",
    DEPT_HEAD: "Department Head",
    PLANT_MANAGER: "Plant Manager",
    PURCHASE_MANAGER: "Purchase",
    DIRECTOR: "Director",
};

const ROLE_BADGE = {
    CORPORATE_ADMIN: "bg-slate-900 text-white border-slate-900",
    FINANCE_PRICING: "bg-amber-50 text-amber-800 border-amber-200",
    DEPT_HEAD: "bg-slate-100 text-slate-700 border-slate-200",
    PLANT_MANAGER: "bg-blue-50 text-blue-700 border-blue-200",
    PURCHASE_MANAGER: "bg-slate-100 text-slate-700 border-slate-200",
    DIRECTOR: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export function roleLabel(role) {
    return ROLE_LABEL[role] || role;
}

export function roleBadgeClass(role) {
    return ROLE_BADGE[role] || "bg-slate-100 text-slate-700 border-slate-200";
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = checking, null = anonymous
    const [tenant, setTenant] = useState(null);

    useEffect(() => {
        // Bootstrap from localStorage
        const token = localStorage.getItem("costiq_token");
        const cached = localStorage.getItem("costiq_user");
        if (token && cached) {
            try {
                setUser(JSON.parse(cached));
            } catch {
                setUser(null);
            }
            // Verify with backend in background
            api.get("/auth/me")
                .then((r) => {
                    setUser(r.data);
                    localStorage.setItem("costiq_user", JSON.stringify(r.data));
                })
                .catch(() => {
                    localStorage.removeItem("costiq_token");
                    localStorage.removeItem("costiq_user");
                    setUser(null);
                });
        } else {
            setUser(null);
        }
        api.get("/auth/tenant").then((r) => setTenant(r.data)).catch(() => {});
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("costiq_token", data.access_token);
            localStorage.setItem("costiq_user", JSON.stringify(data.user));
            setUser(data.user);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: formatApiError(err) };
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("costiq_token");
        localStorage.removeItem("costiq_user");
        setUser(null);
        window.location.href = "/login";
    }, []);

    const value = { user, tenant, login, logout, roleLabel, roleBadgeClass };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}

/** Role-based access helper */
export function hasRole(user, ...allowed) {
    return user && allowed.includes(user.role);
}
